import {
  faAddressBook,
  faPenToSquare,
  faUser,
} from '@fortawesome/pro-regular-svg-icons';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { AlertBox } from '@/common/AlertBox';
import { InfoSectionContainer } from '@/common/InfoSection';
import { SubTabs } from '@/common/SubTabs';
import { TabsBar } from '@/common/TabsBar';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import { WBSButton } from '@/common/WBSButton';
import { EditProfileDetails } from '@/profile/EditProfileDetails';
import { EditProfileSHBoard } from '@/profile/EditProfileSHBoard';
import { EditProfileSocial } from '@/profile/EditProfileSocial';
import { PageContent, PageHeader } from '@/settings/settingsStyles';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { mockUserData } from '~/mockData';
import { formatString } from '~/utils/stringFormat';

export const Route = createFileRoute(
  '/_app/_users/members/$memberId/profile/edit',
)({
  component: EditProfileRoute,
});

function EditProfileRoute() {
  const { t } = useTranslation();
  const { memberId } = Route.useParams();
  const { isStudyHelper } = useCurrentUser();
  const [activeTab, setActiveTab] = useState<
    'details' | 'social' | 'studyHelper'
  >('details');
  const [formData, setFormData] = useState(mockUserData);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value === 'true' ? true : value === 'false' ? false : value,
    }));
  };

  const handleBirthdateChange = (
    field: 'day' | 'month' | 'year',
    value: string,
  ) => {
    setFormData((prev) => ({
      ...prev,
      birthdate: { ...prev.birthdate, [field]: value },
    }));
  };

  const handleLanguagesChange = (languages: string[]) => {
    setFormData((prev) => ({ ...prev, languages }));
  };

  const handlePreferenceToggle = (preference: string) => {
    setFormData((prev) => ({
      ...prev,
      studyHelperPreferences: prev.studyHelperPreferences.includes(preference)
        ? prev.studyHelperPreferences.filter((p) => p !== preference)
        : [...prev.studyHelperPreferences, preference],
    }));
  };

  const handleSaveChanges = () => {
    console.log('Saving changes:', formData);
    // TODO: Add actual API call to save data
    setShowSuccessAlert(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const mainTabs = [
    {
      title: t('actions.edit'),
      to: 'edit',
      icon: faPenToSquare,
    },
    {
      title: t('profile.profile-photo'),
      to: 'change-avatar',
      icon: faAddressBook,
    },
  ];

  const subTabs: Array<{
    id: 'details' | 'social' | 'studyHelper';
    label: string;
  }> = [
    { id: 'details', label: t('wbs.details') },
    { id: 'social', label: t('profile.social-settings') },
    { id: 'studyHelper', label: t('lessons.study-helper-board') },
  ];

  return (
    <PageContent>
      {showSuccessAlert && (
        // Translation Needed
        <AlertBox variant="success">Changes saved.</AlertBox>
      )}
      <PageHeader>
        <TitleWithQuestion title={t('actions.edit-profile')} />
        <Link
          to={
            isStudyHelper
              ? '/members/$memberId/'
              : '/members/$memberId/profile/'
          }
          params={{ memberId }}
        >
          <WBSButton wide alternate icon={faUser}>
            {t('profile.view-my-profile')}
          </WBSButton>
        </Link>
      </PageHeader>

      <div style={{ marginBottom: 11 }}>
        <TabsBar tabs={mainTabs} base="/members/$memberId/profile" />
      </div>
      <InfoSectionContainer>
        <FormTitle>
          {formatString(
            t('actions.edit-s-information'),
            activeTab === 'details'
              ? t('wbs.details')
              : activeTab === 'social'
                ? t('profile.social-settings')
                : t('lessons.study-helper-board'),
          )}
        </FormTitle>

        <SubTabs
          tabs={subTabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />

        {activeTab === 'details' && (
          <EditProfileDetails
            formData={formData}
            onInputChange={handleInputChange}
            onBirthdateChange={handleBirthdateChange}
            onLanguagesChange={handleLanguagesChange}
            onSaveChanges={handleSaveChanges}
          />
        )}

        {activeTab === 'social' && (
          <EditProfileSocial
            formData={{ publicProfile: formData.publicProfile }}
            onInputChange={handleInputChange}
            onSaveChanges={handleSaveChanges}
          />
        )}

        {activeTab === 'studyHelper' && (
          <EditProfileSHBoard
            formData={{
              studyHelperBoard: formData.studyHelperBoard,
              maxStudents: formData.maxStudents,
              studyHelperPreferences: formData.studyHelperPreferences,
            }}
            onInputChange={handleInputChange}
            onPreferenceToggle={handlePreferenceToggle}
            onSaveChanges={handleSaveChanges}
          />
        )}
      </InfoSectionContainer>
    </PageContent>
  );
}

const FormTitle = styled.h2`
  color: var(--grey-900);
  font-family: var(--raleway);
  font-size: 22px;
  font-weight: 400;
  letter-spacing: 0.25px;
  margin: 5px 0 29px 0;
`;
